/**
 * In-lesson Flashcards tab - Anki-style spaced-repetition review.
 *
 * Flip a card, then self-rate (Again/Hard/Good/Easy); rating advances to the
 * next card. On finish, each concept's cards aggregate to ONE FSRS grade via
 * MIN (the weakest card wins, so a fumble isn't masked) and are submitted with
 * the per-lesson daily idempotency key shared with the quiz - so whichever
 * surface grades a concept first wins, and a same-day retry never re-grades.
 *
 * State is persisted (localStorage, keyed by lesson) so a run survives sub-tab
 * switches and reloads: a finished deck shows its summary; an in-progress one
 * resumes. Concepts without a resolved `mem0_memory_id` are shown but not
 * submitted (mindspace sync still in flight).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track } from '@/config/observability';
import { notify } from '@/lib/notify';
import type { ConceptInLesson } from '@/lib/api-client';
import type { Flashcard } from '@/data/fixtures';
import { useSubmitReview } from '@/hooks/mindspace/useRevision';
import {
  useFlashcardSession,
  type PersistedFlashcardSession,
} from '@/hooks/practice/useFlashcardSession';
import { writtenSignature } from '@/lib/practice/signature';
import { practiceFsrsAttemptId } from '@/lib/practice/attemptKey';
import { buildConceptResolver } from '@/lib/practice/concepts';
import {
  minGrade,
  type ConceptGrade,
  type Grade,
  type ReviewOutcomes,
  submitConceptGrades,
} from '@/lib/practice/grades';
import { FlashcardsSkeleton } from '../skeletons/PanelSkeletons';
import { FlashcardDeck } from './FlashcardDeck';
import { FlashcardCompletedView } from './FlashcardCompletedView';

interface LessonFlashcardRunnerProps {
  cards: Flashcard[];
  concepts: ConceptInLesson[];
  lessonId?: string;
  isLoading?: boolean;
  /** Go to the quiz - offered when the deck is done. */
  onTakeQuiz?: () => void;
}

export function LessonFlashcardRunner({
  cards,
  concepts,
  lessonId,
  isLoading,
  onTakeQuiz,
}: LessonFlashcardRunnerProps) {
  const submit = useSubmitReview();

  // Resolves an item's concept_id (per-lesson OR canonical) to its concept +
  // the canonical id FSRS submits against.
  const resolveConcept = useMemo(
    () => buildConceptResolver(concepts),
    [concepts]
  );

  // Picture cards arrive after the deck: never a new session.
  const signature = useMemo(
    () => writtenSignature(cards, c => c.front),
    [cards]
  );
  const { session, update, reset } = useFlashcardSession(
    lessonId ?? 'unknown',
    signature,
    cards.length > 0
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const total = cards.length;
  const index = Math.min(session.index, Math.max(0, total - 1));
  const current = cards[index];

  const submitResults = useCallback(
    async (sess: PersistedFlashcardSession) => {
      setIsSubmitting(true);
      const fsrsAttemptId = practiceFsrsAttemptId(lessonId ?? 'unknown');
      const grades = aggregateFlashcardGrades(cards, sess.ratings);
      const rated = Object.values(sess.ratings).map(Number);
      if (lessonId) {
        track('flashcards_completed', {
          lesson_id: lessonId,
          card_count: cards.length,
          avg_grade: rated.length
            ? rated.reduce((a, b) => a + b, 0) / rated.length
            : 0,
        });
      }
      const { outcomes, failed } = await submitConceptGrades(grades, {
        canonicalId: id => resolveConcept(id)?.canonicalId ?? undefined,
        submit: vars => submit.mutateAsync(vars),
        attemptId: fsrsAttemptId,
        lessonId,
      });
      if (failed > 0) {
        notify.error(
          `Failed to record ${failed} review${failed === 1 ? '' : 's'}`
        );
      }
      update({ submission: 'done', reviewOutcomes: outcomes });
      setIsSubmitting(false);
    },
    [cards, resolveConcept, lessonId, submit, update]
  );

  const firedRef = useRef(false);
  useEffect(() => {
    if (
      session.completed &&
      session.submission === 'idle' &&
      !firedRef.current &&
      cards.length > 0 &&
      concepts.length > 0
    ) {
      firedRef.current = true;
      void submitResults(session);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.completed, session.submission, cards.length, concepts.length]);

  const flip = useCallback(() => {
    update({ revealed: !session.revealed });
  }, [session.revealed, update]);

  const rate = useCallback(
    (grade: Grade) => {
      const nextRatings = { ...session.ratings, [index]: grade };
      if (index >= total - 1) {
        update({ ratings: nextRatings, completed: true });
      } else {
        update({ ratings: nextRatings, index: index + 1, revealed: false });
      }
    },
    [index, total, session.ratings, update]
  );

  const goPrev = useCallback(() => {
    if (index > 0) update({ index: index - 1, revealed: false });
  }, [index, update]);

  // Forward only skips through cards you've already rated - rating is the way
  // to reach a *new* card, so you can't advance past an unrated one.
  const canGoNext = index < total - 1 && session.ratings[index] !== undefined;
  const goNext = useCallback(() => {
    if (canGoNext) update({ index: index + 1, revealed: false });
  }, [canGoNext, index, update]);

  const handleRestart = () => {
    setIsSubmitting(false);
    firedRef.current = false;
    reset();
  };

  // Keyboard: Space flips; 1-4 rate once revealed.
  useEffect(() => {
    if (isLoading || session.completed || total === 0) return;
    // Space / Enter flip; ← → move (the header's arrows); 1-4 rate once
    // the answer is showing.
    const actions: Record<string, () => void> = {
      ' ': flip,
      Enter: flip,
      ArrowLeft: goPrev,
      ArrowRight: goNext,
    };
    const handler = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      const grade = Number(e.key);
      const action =
        actions[e.key] ??
        (session.revealed && grade >= 1 && grade <= 4
          ? () => rate(grade as Grade)
          : undefined);
      if (!action) return;
      e.preventDefault();
      action();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    isLoading,
    session.completed,
    session.revealed,
    total,
    flip,
    rate,
    goPrev,
    goNext,
  ]);

  if (isLoading) {
    return <FlashcardsSkeleton mode='fetching' />;
  }

  if (total === 0) {
    return (
      <div className='flex h-full items-center justify-center text-sm text-muted-foreground'>
        No flashcards available.
      </div>
    );
  }

  if (session.completed) {
    return (
      <FlashcardCompletedView
        rated={Object.keys(session.ratings).length}
        total={total}
        ratings={Object.values(session.ratings)}
        onTakeQuiz={onTakeQuiz}
        conceptGrades={aggregateFlashcardGrades(cards, session.ratings)}
        resolveConcept={resolveConcept}
        outcomes={session.reviewOutcomes}
        submission={isSubmitting ? 'submitting' : session.submission}
        onRestart={handleRestart}
      />
    );
  }

  if (!current) return null;

  return (
    <FlashcardDeck
      card={current}
      index={index}
      total={total}
      revealed={session.revealed}
      rating={session.ratings[index]}
      canGoNext={canGoNext}
      hasProgress={Object.keys(session.ratings).length > 0}
      onFlip={flip}
      onPrev={goPrev}
      onNext={goNext}
      onRate={rate}
      onRestart={handleRestart}
    />
  );
}

// ===========================================================================
// AGGREGATION
// ===========================================================================

/** One grade per concept = MIN of its rated cards' grades (rated cards only). */
function aggregateFlashcardGrades(
  cards: Flashcard[],
  ratings: Record<number, Grade>
): ConceptGrade[] {
  const byConcept = new Map<string, Grade[]>();
  cards.forEach((card, i) => {
    const cid = card.concept_id;
    const grade = ratings[i];
    if (!cid || grade === undefined) return;
    const arr = byConcept.get(cid) ?? [];
    arr.push(grade);
    byConcept.set(cid, arr);
  });

  const out: ConceptGrade[] = [];
  for (const [conceptId, grades] of byConcept.entries()) {
    if (grades.length === 0) continue;
    out.push({ conceptId, grade: minGrade(grades) });
  }
  return out;
}
